import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Target, Zap, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    source: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSourceChange = (value) => {
    setFormData((prev) => ({ ...prev, source: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "", source: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold text-lg tracking-wide text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              AUREALIAN
            </span>
          </div>
          <Button 
            onClick={scrollToForm}
            data-testid="nav-cta-btn"
            className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-xs font-bold px-6 py-2"
          >
            Start Now
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Hero Text - 7 columns */}
            <div className="md:col-span-7 space-y-8 opacity-0 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
                  Transform Your Life
                </span>
              </div>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="hero-title"
              >
                Turn <span className="text-[#D4AF37]">One Day</span><br />
                Into <span className="text-[#D4AF37]">Today</span>
              </h1>
              
              <p className="text-base md:text-lg text-[#A1A1AA] max-w-lg leading-relaxed">
                The Aurealian Method is a transformative coaching experience designed 
                to unlock your potential and ignite lasting change. Stop waiting. Start becoming.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToForm}
                  data-testid="hero-cta-btn"
                  className="btn-primary bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-sm font-bold px-8 py-6 gold-glow"
                >
                  Begin Your Journey
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Hero Image - 5 columns */}
            <div className="md:col-span-5 opacity-0 animate-fade-up animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-sm blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1623685539644-ff3f8f8d2086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxkZXRlcm1pbmVkJTIwcGVyc29uJTIwZ29sZGVuJTIwaG91ciUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MDcxMjg1NHww&ixlib=rb-4.1.0&q=85"
                  alt="Person in golden light"
                  className="relative w-full h-[400px] md:h-[500px] object-cover rounded-sm hero-image"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 opacity-0 animate-fade-up">
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The Method
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              A proven framework for breakthrough results
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Clarity",
                desc: "Define your vision with precision. Know exactly where you're going and why it matters.",
              },
              {
                icon: Zap,
                title: "Action",
                desc: "Break through resistance. Transform intention into consistent, powerful action.",
              },
              {
                icon: Sparkles,
                title: "Mastery",
                desc: "Develop unstoppable momentum. Build habits that compound into extraordinary results.",
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className={`p-8 bg-[#121212] border border-[#27272A]/40 hover:border-[#D4AF37]/50 transition-all duration-300 rounded-sm opacity-0 animate-fade-up`}
                style={{ animationDelay: `${(idx + 1) * 100}ms` }}
                data-testid={`feature-card-${idx}`}
              >
                <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 
                  className="text-xl font-bold text-white mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {feature.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Background */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1628243900589-625069dd142e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGRlbiUyMGxpZ2h0JTIwYnJlYWt0aHJvdWdofGVufDB8fHx8MTc3MDcxMjg1OHww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center opacity-0 animate-fade-up">
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Your Breakthrough Awaits
            </h2>
            <p className="text-base md:text-lg text-[#A1A1AA] leading-relaxed mb-8">
              Every transformation begins with a single decision. The Aurealian Method 
              combines strategic coaching, accountability systems, and proven frameworks 
              to help you achieve what you've always known was possible.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {["Personalized Strategy", "Weekly Accountability", "Breakthrough Sessions"].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#D4AF37]">
                  <Check className="w-5 h-5" />
                  <span className="text-sm uppercase tracking-wider">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Form Info */}
            <div className="opacity-0 animate-fade-up">
              <h2 
                className="text-3xl md:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Ready to Transform?
              </h2>
              <p className="text-[#A1A1AA] mb-8 leading-relaxed">
                Take the first step. Share your details and we'll reach out to discuss 
                how The Aurealian Method can help you achieve your goals.
              </p>
              
              <div className="space-y-4">
                {[
                  "Free initial consultation",
                  "No obligation to commit",
                  "Personalized approach discussion",
                  "Clear next steps provided",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#D4AF37]" />
                    </div>
                    <span className="text-[#EDEDED]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="opacity-0 animate-fade-up animation-delay-200">
              {submitted ? (
                <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-sm p-12 text-center" data-testid="success-message">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 
                    className="text-2xl font-bold text-white mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Message Received
                  </h3>
                  <p className="text-[#A1A1AA]">
                    Thank you for reaching out. We'll be in touch within 24 hours.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 bg-transparent border border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-none uppercase tracking-widest text-xs px-6 py-2"
                    data-testid="submit-another-btn"
                  >
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form 
                  onSubmit={handleSubmit}
                  className="bg-[#121212] border border-[#27272A]/40 rounded-sm p-8 space-y-6"
                  data-testid="lead-form"
                >
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      Full Name <span className="text-[#D4AF37]">*</span>
                    </Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                      data-testid="input-name"
                      className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white placeholder:text-[#52525B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      Email <span className="text-[#D4AF37]">*</span>
                    </Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                      data-testid="input-email"
                      className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white placeholder:text-[#52525B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      Phone <span className="text-[#D4AF37]">*</span>
                    </Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      required
                      data-testid="input-phone"
                      className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white placeholder:text-[#52525B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      How did you hear about us?
                    </Label>
                    <Select value={formData.source} onValueChange={handleSourceChange}>
                      <SelectTrigger 
                        data-testid="select-source"
                        className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm h-12 text-white"
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        <SelectItem value="social_media">Social Media</SelectItem>
                        <SelectItem value="search_engine">Search Engine</SelectItem>
                        <SelectItem value="referral">Friend/Referral</SelectItem>
                        <SelectItem value="podcast">Podcast</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">
                      What are your goals?
                    </Label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about what you want to achieve..."
                      rows={4}
                      data-testid="input-message"
                      className="bg-[#1F1F1F] border-transparent focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm text-white placeholder:text-[#52525B] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                    className="w-full btn-primary bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90 rounded-none uppercase tracking-widest text-sm font-bold py-6 gold-glow disabled:opacity-50"
                  >
                    {isSubmitting ? "Sending..." : "Get Started"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#D4AF37] rounded-sm flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-black" />
              </div>
              <span className="text-sm text-[#A1A1AA]">
                The Aurealian Method
              </span>
            </div>
            <p className="text-xs text-[#52525B]">
              &copy; {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
